import React from 'react';
import I18n from 'i18n-js';

import Modal from '@reverbdotcom/commons/src/components/modal';
import {
  BuyItNowPaymentOptionsFields,
  core_apimessages_CheckoutPaymentMethod_Type as CheckoutPaymentMethodType,
  core_apimessages_Error,
} from '../schemas/rql';
import PaymentSelectionModalPaymentMethod from './payment_selection_modal_payment_method';
import gql from 'graphql-tag';
import { RCAlertBox, RCLoadingBars } from '@reverbdotcom/cadence/components';
import { BuyListingNowApplePayFragment } from './buy_listing_now_apple_pay_button';
import { CheckoutItemPageReturnError } from './checkout_item_page_return_layout';
import SignupSigninModal from '@reverbdotcom/commons/src/components/signup_signin_modal';
import { useUser, useExpEnabled } from '@reverbdotcom/commons/src/user_hooks';
import experiments from '@reverbdotcom/commons/src/experiments';
import BuyItNowApplePayWrapper from './BuyItNowApplePayWrapper';
import { filterBuyerPaymentMethods } from '../lib/klarnaExperiments';
import BuyItNowExpressPayWrapper from './BuyItNowExpressPayWrapper';

const DC_VIA_PAYPAL = 'dc_via_paypal';
const APPLE_PAY = 'apple_pay';

type Listing = BuyItNowPaymentOptionsFields.Fragment;

interface Props {
  listing: Listing;
  quantity: number;
  modalOpen: boolean;
  onRequestClose: () => any;
  onStartCheckoutSuccess: (redirectUrl: string, paymentMethodType: CheckoutPaymentMethodType) => any;
}

export function BuyItNowPaymentOptions(props: Props) {
  const [isLoading, setIsLoading] = React.useState(false);
  const [paymentMethodErrors, setPaymentMethodErrors] = React.useState([]);
  const [applePayError, setApplePayError] = React.useState('');
  const [showAuthModal, setShowAuthModal] = React.useState(false);
  const user = useUser();
  const { listing, quantity } = props;
  const acceptedPaymentMethods = useAcceptedPaymentMethods(listing?.acceptedPaymentMethods);

  const inPaypalButtonRollout = useExpEnabled(experiments.PAYPAL_CHECKOUT_BUTTON);
  const showPaypalCheckoutButton = acceptedPaymentMethods.includes(DC_VIA_PAYPAL) && inPaypalButtonRollout;
  const showExpressPay = inPaypalButtonRollout
    && [DC_VIA_PAYPAL, APPLE_PAY].some(paymentMethod => acceptedPaymentMethods.includes(paymentMethod));

  const buyerAndSellerInSameCountry = user.countryCode === listing.shop?.address?.countryCode;
  const sellerCountry = listing.shop?.address?.country?.name;

  const onError = React.useCallback((errorType: CheckoutItemPageReturnError, message: string) => {
    if (['PAYMENT_FAILURE', 'CHECKOUT_FAILURE'].includes(errorType)) {
      setApplePayError(message);
    } else if (errorType === 'STANDARD_USER_ATTEMPTED_GUEST_CHECKOUT_FAILURE') {
      setShowAuthModal(true);
      setApplePayError(message);
    }
  }, [setShowAuthModal, setApplePayError]);

  if (!listing) return null;

  const modalError = !acceptedPaymentMethods.length;

  function onRequestClose() {
    setApplePayError('');
    setShowAuthModal(false);
    setPaymentMethodErrors([]);
    props.onRequestClose();
  }

  function paymentMethodToListItem(paymentMethod: string) {
    if (paymentMethod === APPLE_PAY && showExpressPay) { return null; }

    if (paymentMethod === APPLE_PAY) {
      return (
        <li key={paymentMethod} className="payment-method-button__wrapper">
          <BuyItNowApplePayWrapper
            listingId={listing.id}
            listingCurrencyCode={listing.currency}
            paymentMethodsConfig={listing.adyenCheckoutPaymentMethodsConfig}
            setIsLoading={setIsLoading}
            isDisabled={isLoading}
            onError={onError}
          />
        </li>
      );
    }

    if (inPaypalButtonRollout && paymentMethod === DC_VIA_PAYPAL) {
      return null;
    }

    const checkoutPaymentMethod = CheckoutPaymentMethodType[paymentMethod.toUpperCase()];

    return (
      <li key={paymentMethod} className="payment-method-button__wrapper">
        <PaymentSelectionModalPaymentMethod
          paymentMethod={checkoutPaymentMethod}
          key={paymentMethod}
          listingId={listing.id}
          onSuccess={props.onStartCheckoutSuccess}
          onError={setPaymentMethodErrors}
          setLoading={setIsLoading}
          isDisabled={isLoading}
        />
      </li>
    );
  }

  if (!showAuthModal) {
    return (
      <Modal
        isOpen={props.modalOpen}
        onRequestClose={onRequestClose}
        headerTitle={I18n.t('discovery.paymentMethodSelection.choosePaymentMethod')}
        paddedBody
      >
        {!buyerAndSellerInSameCountry &&
          <div className="mb-2">
            <RCAlertBox type="info">
              {I18n.t('discovery.checkout.buyItNow.notInSameCountry', {
                sellerCountry: sellerCountry,
              })}
            </RCAlertBox>
          </div>
        }
        {modalError &&
          <RCAlertBox type="error">
            {I18n.t('discovery.checkout.buyItNow.error')}
          </RCAlertBox>
        }
        {applePayError &&
          <div className="mb-2">
            <RCAlertBox
              data-apple-pay-alert-box
              type="error"
            >
              {applePayError}
            </RCAlertBox>
          </div>
        }
        {paymentMethodErrors.length > 0 &&
          <div className="mb-2">
            <ErrorAlert errors={paymentMethodErrors} />
          </div>
        }
        {isLoading &&
          <div className="d-flex fx-justify-center mtb-1">
            <RCLoadingBars />
          </div>
        }
        {acceptedPaymentMethods.length > 0 &&
          <div className="cart-payment-methods">
            {showExpressPay &&
              <BuyItNowExpressPayWrapper
                listing={listing}
                showPaypalCheckoutButton={showPaypalCheckoutButton}
                adyenCheckoutPaymentMethodsConfig={listing.adyenCheckoutPaymentMethodsConfig}
                setIsLoading={setIsLoading}
                isLoading={isLoading}
                onError={setPaymentMethodErrors}
                quantity={quantity}
              />
            }
            <ul>
              {acceptedPaymentMethods.map(paymentMethodToListItem)}
            </ul>
          </div>
        }
      </Modal>
    );
  } else {
    return (
      <SignupSigninModal
        messageComponent={
          <div className="mb-2">
            <RCAlertBox type="warning" small>
              {applePayError}
            </RCAlertBox>
          </div>
        }
        active={showAuthModal}
        onRequestClose={onRequestClose}
        showSignInOnOpen
      />
    );
  }
}

function useAcceptedPaymentMethods(acceptedPaymentMethods?: string[]) {
  const user = useUser();
  if (!acceptedPaymentMethods) { return []; }

  const filteredPaymentMethods = filterBuyerPaymentMethods(acceptedPaymentMethods, user);

  if (filteredPaymentMethods.includes(APPLE_PAY)) {
    return prioritizeApplePayWithPaymentMethods(filteredPaymentMethods);
  }

  return filteredPaymentMethods;
}

function prioritizeApplePayWithPaymentMethods(acceptedPaymentMethods: string[]) {
  const paymentMethodsWithoutApplePay = acceptedPaymentMethods.filter(m => m !== APPLE_PAY);

  return [APPLE_PAY, ...paymentMethodsWithoutApplePay];
}

function ErrorAlert({ errors }: { errors: core_apimessages_Error[] }) {
  return (
    <RCAlertBox type="error">
      {errors.map((error) => {
        return (
          <li key={error.field + error.message}>
            {error.message}
          </li>
        );
      })}
    </RCAlertBox>
  );
}

export const BuyItNowPaymentOptionsFragment = gql`
  fragment BuyItNowPaymentOptionsFields on Listing {
    _id
    acceptedPaymentMethods
    sellerId
    shop {
      _id
      address {
        countryCode
        country {
          name
        }
      }
    }
    ...BuyListingNowApplePayData
  }
  ${BuyListingNowApplePayFragment}
`;
